import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, ArrowLeft, KeyRound } from "lucide-react";
import { Input } from "@/components/ui/input";
import { api, apiError } from "@/lib/api";
import { toast } from "@/components/ui/sonner";

export default function ResellerLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState("login"); // login | forgot | reset
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        const { data } = await api.post("/reseller/login", { email, password });
        localStorage.setItem("desync_reseller_token", data.token);
        navigate("/reseller");
        return;
      }
      if (mode === "forgot") {
        await api.post("/reseller/forgot-password", { email });
        toast.success("If that email is a reseller, a reset code is on its way");
        setMode("reset");
      } else {
        await api.post("/reseller/reset-password", { email, code, new_password: newPassword });
        toast.success("Password updated — sign in with it");
        setMode("login");
        setPassword("");
      }
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2" data-testid="reseller-login-page">
      <div className="relative hidden lg:flex flex-col justify-between p-12 overflow-hidden">
        <img src="/images/hero-gta.png" alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#050B18]/60 to-[#050B18]" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050B18] via-transparent to-[#050B18]/40" />
        <div className="relative flex items-center gap-2.5">
          <img src="/images/logo.svg" alt="Desync logo" className="w-9 h-9" />
          <span className="font-display font-extrabold tracking-tight text-xl">
            De<span className="text-[#2E6BFF]">sync</span>
          </span>
        </div>
        <div className="relative">
          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="font-display font-extrabold tracking-tight text-4xl xl:text-5xl leading-tight text-white"
          >
            Wholesale,
            <span className="block text-[#2E6BFF] text-glow-blue">your way.</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="mt-4 text-slate-400 max-w-sm"
          >
            Your own pricing, instant key delivery, one wallet for everything.
          </motion.p>
        </div>
        <div className="relative flex items-center gap-2 text-xs text-slate-500">
          <span className="relative flex h-2 w-2">
            <span className="status-dot relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
          </span>
          Reseller portal
        </div>
      </div>

      <div className="relative flex items-center justify-center p-6">
        <div className="absolute inset-0 grid-overlay opacity-40 lg:hidden" />
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="relative w-full max-w-sm"
        >
          <div className="flex lg:hidden items-center gap-2.5 mb-10 justify-center">
            <img src="/images/logo.svg" alt="Desync logo" className="w-9 h-9" />
            <span className="font-display font-extrabold tracking-tight text-xl">
              De<span className="text-[#2E6BFF]">sync</span>
            </span>
          </div>

          <div className="p-8 rounded-2xl bg-[#0A1628] border border-[#1E2D4A] shadow-[0_20px_60px_rgba(0,0,0,0.4)]">
            <h2 className="font-display text-2xl font-bold tracking-tight text-white">
              {mode === "login" ? "Reseller sign in" : "Reset your password"}
            </h2>
            <p className="text-sm text-slate-500 mt-1 mb-8">
              {mode === "login"
                ? "Approved reseller accounts only"
                : mode === "forgot"
                  ? "We'll email you a 6-digit reset code"
                  : "Enter the code from your email and a new password"}
            </p>

            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="text-sm text-slate-300 block mb-2">Email</label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  data-testid="reseller-email-input"
                  className="bg-[#050B18] border-[#1E2D4A] focus-visible:ring-[#2E6BFF] h-11"
                />
              </div>
              {mode === "login" && (
                <div>
                  <label className="text-sm text-slate-300 block mb-2">Password</label>
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                    data-testid="reseller-password-input"
                    className="bg-[#050B18] border-[#1E2D4A] focus-visible:ring-[#2E6BFF] h-11"
                  />
                </div>
              )}
              {mode === "reset" && (
                <>
                  <div>
                    <label className="text-sm text-slate-300 block mb-2">Reset code</label>
                    <Input
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                      inputMode="numeric"
                      maxLength={6}
                      data-testid="reseller-reset-code-input"
                      className="bg-[#050B18] border-[#1E2D4A] focus-visible:ring-[#2E6BFF] h-11 font-mono tracking-[0.4em] text-center"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-slate-300 block mb-2">New password</label>
                    <Input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      autoComplete="new-password"
                      data-testid="reseller-new-password-input"
                      className="bg-[#050B18] border-[#1E2D4A] focus-visible:ring-[#2E6BFF] h-11"
                    />
                  </div>
                </>
              )}
              <button
                type="submit"
                disabled={loading || !email.trim() || (mode === "login" && !password) || (mode === "reset" && (!code || !newPassword))}
                data-testid="reseller-login-submit"
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-lg bg-[#2E6BFF] hover:bg-[#1D55E0] text-white text-sm font-semibold shadow-[0_8px_24px_rgba(46,107,255,0.35)] disabled:opacity-40 transition-all duration-200 active:scale-95"
              >
                {mode === "login" ? <Lock className="w-4 h-4" /> : <KeyRound className="w-4 h-4" />}
                {loading ? "Working..." : mode === "login" ? "Sign in" : mode === "forgot" ? "Email me a code" : "Set new password"}
              </button>
            </form>

            <div className="mt-4 text-center">
              {mode === "login" ? (
                <button
                  onClick={() => setMode("forgot")}
                  data-testid="reseller-forgot-link"
                  className="text-xs text-slate-500 hover:text-[#7FB0FF] transition-colors"
                >
                  Forgot your password?
                </button>
              ) : (
                <button
                  onClick={() => setMode("login")}
                  data-testid="reseller-back-to-login"
                  className="text-xs text-slate-500 hover:text-[#7FB0FF] transition-colors"
                >
                  Back to sign in
                </button>
              )}
            </div>
          </div>

          <Link
            to="/"
            className="mt-6 flex items-center justify-center gap-2 text-sm text-slate-500 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Back to store
          </Link>
        </motion.div>
      </div>
    </div>
  );
}
